"use client";

import { Heart, Leaf, ShieldCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const promises = [{
    icon: Leaf,
    stickerBg: "bg-background",
    title: "Residential Precision",
    description: "Expert wiring, lighting installations, and panel upgrades designed to keep your home safe and efficient."
  }, {
    icon: ShieldCheck,
    stickerBg: "bg-background",
    title: "Commercial Reliability",
    description: "Industrial-grade electrical solutions for businesses, ensuring uptime and compliance with safety standards."
  }, {
    icon: Heart,
    stickerBg: "bg-background",
    title: "Emergency Response",
    description: "24/7 rapid deployment for critical electrical failures, restoring power and safety when you need it most."
  }];
  return <section data-section-id="1395" id="the-current-pulse-advantage" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div ref={ref} className={`mx-auto max-w-7xl px-6 md:px-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative mx-auto w-full max-w-[280px] sm:max-w-[360px] lg:max-w-none aspect-square">
              {}
              <div className="absolute inset-0 bg-secondary border-[1.5px] border-foreground" style={{
              boxShadow: "4px 4px 0 var(--foreground)"
            }} />
              {}
              <div className="absolute inset-x-6 top-10 bottom-10 bg-primary border-[1.5px] border-foreground -rotate-6" style={{
              boxShadow: "4px 4px 0 var(--foreground)"
            }} />
              {}
              <div className="absolute inset-x-10 top-16 bottom-6 bg-background border-[1.5px] border-foreground rotate-3 flex items-center justify-center p-6" style={{
              boxShadow: "4px 4px 0 var(--foreground)"
            }}>
                {}
                <svg viewBox="0 0 200 220" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Current Pulse Electric mascot">
                  {}
                  <line x1="100" y1="30" x2="100" y2="55" stroke="hsl(215 60% 12%)" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="100" cy="24" r="8" fill="hsl(54 100% 50%)" stroke="hsl(215 60% 12%)" strokeWidth="2.5" />
                  {}
                  <rect x="50" y="55" width="100" height="85" rx="0" fill="hsl(215 60% 12%)" stroke="hsl(54 100% 50%)" strokeWidth="2.5" />
                  {}
                  <circle cx="78" cy="92" r="6" fill="hsl(54 100% 50%)" />
                  <circle cx="122" cy="92" r="6" fill="hsl(54 100% 50%)" />
                  {}
                  <circle cx="68" cy="110" r="5" fill="hsl(0 84% 60%)" opacity="0.7" />
                  <circle cx="132" cy="110" r="5" fill="hsl(0 84% 60%)" opacity="0.7" />
                  {}
                  <path d="M 88 115 Q 100 122 112 115" stroke="hsl(54 100% 50%)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                  {}
                  <rect x="65" y="140" width="70" height="55" rx="0" fill="hsl(215 60% 12%)" stroke="hsl(54 100% 50%)" strokeWidth="2.5" />
                  {}
                  <path d="M 138 155 Q 158 145 148 120 Q 142 108 128 112" stroke="hsl(54 100% 50%)" strokeWidth="2.5" fill="hsl(215 60% 12%)" strokeLinecap="round" />
                  {}
                  <g transform="translate(45 160) rotate(-20)">
                    <path d="M 0 0 Q 8 -12 18 -8 Q 20 4 8 8 Q -2 6 0 0 Z" fill="hsl(54 100% 50%)" stroke="hsl(215 60% 12%)" strokeWidth="2" />
                    <line x1="2" y1="2" x2="14" y2="-4" stroke="hsl(215 60% 12%)" strokeWidth="1.5" />
                  </g>
                  {}
                  <circle cx="160" cy="55" r="10" fill="hsl(210 10% 96%)" stroke="hsl(215 60% 12%)" strokeWidth="2" />
                  <circle cx="148" cy="72" r="4" fill="hsl(210 10% 96%)" stroke="hsl(215 60% 12%)" strokeWidth="1.5" />
                </svg>
              </div>

              {}
              <svg className="hidden lg:block absolute -right-10 top-1/2 -translate-y-1/2 w-24 h-16" viewBox="0 0 100 60" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M 5 30 Q 30 10 60 30 T 90 30" stroke="hsl(54 100% 50%)" strokeWidth="2.5" strokeLinecap="round" fill="none" strokeDasharray="4 4" />
                <path d="M 82 22 L 92 30 L 82 38" stroke="hsl(54 100% 50%)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 order-1 lg:order-2">
            {}
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono uppercase font-medium text-foreground" style={{
              fontSize: "clamp(0.6875rem, 0.85vw, 0.8125rem)",
              letterSpacing: "0.18em"
            }}>
                [01 // 03] · OUR EXPERTISE
              </span>
              <span className="inline-block w-3 h-3 bg-primary" />
            </div>

            {}
            <h2 className="font-default font-bold text-foreground mb-10" style={{
            fontSize: "clamp(2rem, 4.4vw, 3.75rem)",
            letterSpacing: "-0.03em",
            lineHeight: "1.1"
          }}>
              Powering your world with{" "}
              <span className="relative inline-block text-primary">
                precision
                <svg className="absolute -inset-2 w-[calc(100%+1rem)] h-[calc(100%+1rem)] pointer-events-none" viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" preserveAspectRatio="none">
                  <path d="M 60 6 C 90 6 114 16 114 30 C 114 46 88 54 60 54 C 30 54 6 46 6 30 C 6 16 28 8 60 6 Z" stroke="hsl(54 100% 50%)" strokeWidth="3" strokeLinecap="round" fill="none" strokeDasharray="400" strokeDashoffset={isVisible ? "0" : "400"} style={{
                  transition: "stroke-dashoffset 600ms ease-out 200ms"
                }} />
                </svg>
              </span>{" "}
              and safety.
            </h2>

            {}
            <ul className="space-y-2">
              {promises.map((promise, i) => {
              const Icon = promise.icon;
              return <li data-index={i} key={i} className="group">
                    <div className="grid grid-cols-12 gap-4 md:gap-5 items-start py-5 transition-all duration-200 ease-out">
                      <div className="col-span-2 sm:col-span-1 flex justify-start">
                        <div className={`relative w-12 h-12 md:w-14 md:h-14 ${promise.stickerBg} border-[1.5px] border-foreground flex items-center justify-center transition-all duration-[220ms] ease-out group-hover:-translate-y-0.5`} style={{
                      boxShadow: "4px 4px 0 var(--foreground)"
                    }}>
                          <Icon className="w-6 h-6 md:w-7 md:h-7 text-foreground" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </div>
                      </div>
                      <div className="col-span-10 sm:col-span-11">
                        <h3 className="font-default font-bold text-foreground mb-2" style={{
                      fontSize: "clamp(1.125rem, 1.6vw, 1.375rem)",
                      letterSpacing: "-0.02em",
                      lineHeight: "1.2"
                    }}>
                          {promise.title}
                        </h3>
                        <p className="font-default text-muted-foreground" style={{
                      fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                      lineHeight: "1.65"
                    }}>
                          {promise.description}
                        </p>
                      </div>
                    </div>
                    {}
                    {i < promises.length - 1 && <svg className="w-full h-2" viewBox="0 0 400 8" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M 0 4 Q 50 0 100 4 T 200 4 T 300 4 T 400 4" stroke="hsl(210 5% 30% / 0.2)" strokeWidth="2" strokeLinecap="round" strokeDasharray="8 6" fill="none" />
                      </svg>}
                  </li>;
            })}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}